import React, { useRef, useEffect } from "react";
import html2canvas from "html2canvas";
import { useTranslation } from "react-i18next";
import styles from"../styles/dashboard.module.css";
import BarChartSVG from "./homeComponents/BarChartSVG";

function ChartToImage({ dashboardData, onCapture, type = "bar", language = "es" }) {
  const chartRef = useRef();
  
  // Calcular altura para gráfico de índice: misma altura de barra que el completo pero con menos espacio
  let chartHeight = 721;
  if (type === "index") {
    // Calcular altura de barra igual al gráfico completo
    const mFull = { top: 80, bottom: 80 };
    const chartHFull = 721 - mFull.top - mFull.bottom; // 561
    const groupCount = 4;
    const rowHFull = chartHFull / groupCount; // 140.25
    const barGapFrac = 0.3;
    const barsAreaHFull = rowHFull * (1 - barGapFrac); // 98.175
    const barGap = 6;
    const barH = (barsAreaHFull - barGap) / 2; // 46.0875
    
    // Calcular la posición Y de la primera barra del gráfico completo para alinearla
    const rowTopFull = mFull.top + 0 * rowHFull; // 80
    const rowCenterFull = rowTopFull + rowHFull / 2; // 150.125
    const teamYCenterFull = rowCenterFull - (barH / 2 + barGap / 2); // 124.08125
    
    // Altura total: necesitamos espacio arriba para triángulos y abajo para números
    const spaceForTriangles = 40;
    const spaceForTicks = 50;
    const minMarginTop = teamYCenterFull - spaceForTriangles - barH / 2;
    const minMarginBottom = 30;
    chartHeight = minMarginTop + spaceForTriangles + barH + spaceForTicks + minMarginBottom;
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      // Forzar tamaño fijo y escala para máxima resolución
      const forcedWidth = 1441;
      const forcedHeight = chartHeight;
      html2canvas(chartRef.current, {
        backgroundColor: null,
        width: forcedWidth,
        height: forcedHeight,
        scale: window.devicePixelRatio || 2
      }).then((canvas) => {
        canvas.toBlob((blob) => {
          onCapture(blob);
        });
      }).catch((error) => {
        console.error('Error al convertir chart a imagen:', error);
      });
    }, 500); // Espera a que se monte el DOM

    return () => clearTimeout(timer);
  }, [onCapture, chartHeight]);

  return (
    <div style={{ position: "absolute", left: "-9999px", top: 0, overflow: "visible" }}>
      <div ref={chartRef} style={{ width: 1441, height: chartHeight, overflow: "visible" }}>
        {/* Por ahora solo se usa el gráfico de barras, el de araña está comentado */}
        {type === "bar" ? (
          <BarChartSVG dashboardData={dashboardData} responsive={false} language={language} />
        ) : type === "index" ? (
          <IndexChartComponent dashboardData={dashboardData} language={language} />
        ) : (
          // <RadarChartComponent dashboardData={dashboardData} />
          null
        )}
      </div>
    </div>
  );
}
function IndexChartComponent({ dashboardData, language = "es" }) {
  const { t, i18n } = useTranslation();
  const tFixed = i18n.getFixedT(language);

  if (!dashboardData) {
    return (
      <div className={styles.divPibito}>
        <img className={styles.pibito} src="/img/Pibito.png" alt="No data" />
        <p className={styles.noData}>{tFixed("noData")}</p>
      </div>
    );
  }

  // Calcular la media de las 4 dimensiones para cada serie
  const calculateAverage = (data) => {
    if (!data) return 0;
    const values = [data.Goals, data.Roles, data.Processes, data.Interactions]
      .filter((v) => v != null && v !== undefined && !Number.isNaN(Number(v)))
      .map(Number);
    if (values.length === 0) return 0;
    return values.reduce((sum, v) => sum + v, 0) / values.length;
  };

  // Calcular la media de las desviaciones estándar
  const calculateStdAverage = (stdData) => {
    if (!stdData) return 0;
    const values = [
      stdData.Goals || 0,
      stdData.Roles || 0,
      stdData.Processes || 0,
      stdData.Interactions || 0
    ].filter(v => v !== null && v !== undefined && v > 0);
    if (values.length === 0) return 0;
    return values.reduce((sum, v) => sum + v, 0) / values.length;
  };

  const teamAvg = calculateAverage(dashboardData.mediaEquipo);
  const companyAvg = calculateAverage(dashboardData.mediaEquipoEmpresa);
  const benchmarkAvg = calculateAverage(dashboardData.mediaEquipoGlobal);
  
  const teamStd = calculateStdAverage(dashboardData.desviacionEquipo);

  const referenceData = {
    benchmark: benchmarkAvg,
    company: companyAvg,
  };

  const W = 1441;
  
  // Calcular la altura de la barra usando exactamente los mismos valores que el gráfico completo
  const mFull = { top: 80, right: 160, bottom: 80, left: 260 };
  const chartHFull = 721 - mFull.top - mFull.bottom; // 561
  const groupCount = 4;
  const rowHFull = chartHFull / groupCount; // 140.25
  const barGapFrac = 0.3;
  const barsAreaHFull = rowHFull * (1 - barGapFrac); // 98.175
  const barGap = 6;
  const barH = (barsAreaHFull - barGap) / 2; // 46.0875 - misma altura que el gráfico completo
  
  // Calcular la posición Y de la primera barra del gráfico completo para alinearla
  const rowTopFull = mFull.top + 0 * rowHFull; // 80 (primera fila)
  const rowCenterFull = rowTopFull + rowHFull / 2; // 80 + 70.125 = 150.125
  const teamYCenterFull = rowCenterFull - (barH / 2 + barGap / 2); // 150.125 - 26.04375 = 124.08125
  
  // Calcular altura total: necesitamos espacio arriba para triángulos y abajo para números
  const spaceForTriangles = 40; // espacio para triángulos arriba
  const spaceForTicks = 50; // espacio aumentado para números del eje X abajo
  const minMarginTop = teamYCenterFull - spaceForTriangles - barH / 2; // Posición para alinear la barra
  const minMarginBottom = 30; // Margen aumentado para que los números no se corten
  const H = minMarginTop + spaceForTriangles + barH + spaceForTicks + minMarginBottom;
  
  // Márgenes, margen izquierdo igual al gráfico completo para alineación
  const m = { top: minMarginTop, right: 160, bottom: minMarginBottom, left: 260 };
  const chartW = W - m.left - m.right;
  const chartH = H - m.top - m.bottom;

  const maxVal = 4;
  const xScale = v => m.left + (v / maxVal) * chartW;
  
  // Posicionar la barra en la misma posición Y que la primera barra del gráfico completo
  const teamYCenter = teamYCenterFull; // Misma posición absoluta
  const rowCenter = teamYCenter;

  const yTopTeam = teamYCenter - barH / 2;
  const tipY = m.top + 5; // Triángulos cerca del margen superior
  const triW = 25;
  const triH = 17;
  const offset = 22;

  function textInsideBar(label, x0, x1, yCenter, barH) {
    const padding = 12;
    const tx = x0 + padding;
    const ty = yCenter + 7;

    if ((x1 - x0) < 80) return null;

    return (
      <text
        x={tx}
        y={ty}
        fill="#ffffff"
        fontSize={20}
        fontWeight="500"
      >
        {label}
      </text>
    );
  }

  // Generar ticks del eje X - solo líneas hasta donde está la barra
  const tickTop = m.top;
  const tickBottom = teamYCenter + barH / 2 + 5; // Hasta un poco más abajo de la barra
  const xTicksVals = [0, 1, 2, 3];
  const xTicks = xTicksVals.map(v => {
    const x = xScale(v);
    return (
      <g key={v}>
        <line
          x1={x}
          y1={tickTop}
          x2={x}
          y2={tickBottom}
          stroke="#a3a3a3"
          strokeDasharray="4 4"
        />
        <text
          x={x}
          y={H - m.bottom + 15}
          textAnchor="middle"
          style={{ fontSize: 20, fill: "#595959" }}
        >
          {v}
        </text>
      </g>
    );
  });

  const x4 = xScale(4);
  const xEndAxis = (
    <g>
      <line
        x1={x4}
        y1={tickTop}
        x2={x4}
        y2={tickBottom}
        stroke="#a3a3a3"
        strokeDasharray="4 4"
      />
      <text
        x={x4}
        y={H - m.bottom + 15}
        textAnchor="middle"
        style={{ fontSize: 20, fill: "#595959" }}
      >
        4
      </text>
    </g>
  );

  const elems = [];

  // Triángulo amarillo (company)
  if (referenceData.company != null) {
    const xC = xScale(referenceData.company);
    elems.push(
      <polygon
        key="company"
        points={`
          ${xC},${tipY + triH} 
          ${xC - triW/2},${tipY}
          ${xC + triW/2},${tipY}
        `}
        fill="#FFD700"
      />
    );
  }

  // Triángulo azul (benchmark)
  if (referenceData.benchmark != null) {
    const xB = xScale(referenceData.benchmark) + offset;
    elems.push(
      <polygon
        key="bench"
        points={`
          ${xB},${tipY + triH}
          ${xB - triW/2},${tipY}
          ${xB + triW/2},${tipY}
        `}
        fill="#87CEEB"
      />
    );
  }

  // Solo barra de Equipo
  const avg = Math.min(4, Math.max(0, teamAvg));
  const std = teamStd ?? 0;

  const x0 = xScale(0);
  const xAvg = xScale(avg);

  const yTop = teamYCenter - barH / 2;
  const yBottom = teamYCenter + barH / 2;
  const radius = 6;

  const d = [
    `M ${x0},${yBottom}`,
    `L ${x0},${yTop}`,
    `L ${xAvg - radius},${yTop}`,
    `A ${radius},${radius} 0 0 1 ${xAvg},${yTop + radius}`,
    `L ${xAvg},${yBottom}`,
    `Z`,
  ].join(" ");

  elems.push(
    <g key="bar-equipo">
      <path d={d} fill="#d31273" />
      {textInsideBar(tFixed("team"), x0, xAvg, teamYCenter, barH)}

      {/* Desviación estándar eliminada - no se muestra en el gráfico de índice */}
      
      {/* Puntuación ANTES de la barra (a la izquierda de x0, con gap del título) */}
      <text
        x={x0 - 20}
        y={teamYCenter + 6}
        textAnchor="end"
        fill="#595959"
        fontSize={20}
        fontWeight="bold"
      >
        {avg.toFixed(2)}
      </text>
    </g>
  );

  // Etiqueta del eje Y en tres líneas horizontales (mismo margen izquierdo que el gráfico completo)
  // Usar traducciones según el idioma de la encuesta
  const isEnglish = language === 'en';
  const yAxisLabels = isEnglish
    ? ['Team', 'effectiveness', 'index']
    : ['Índice de', 'efectividad', 'de equipo'];
  
  elems.push(
    <g key="label">
      <text
        x={m.left - 80}
        y={rowCenter - 16}
        textAnchor="end"
        style={{ fontSize: 22, fill: "#595959" }}
      >
        {yAxisLabels[0]}
      </text>
      <text
        x={m.left - 80}
        y={rowCenter + 4}
        textAnchor="end"
        style={{ fontSize: 22, fill: "#595959" }}
      >
        {yAxisLabels[1]}
      </text>
      <text
        x={m.left - 80}
        y={rowCenter + 24}
        textAnchor="end"
        style={{ fontSize: 22, fill: "#595959" }}
      >
        {yAxisLabels[2]}
      </text>
    </g>
  );

  return (
    <svg width={W} height={H}>
      {xTicks}
      {xEndAxis}
      {elems}
    </svg>
  );
}




// Gráfico de araña comentado - por ahora no se usará
/*
function RadarChartComponent({ dashboardData }) {
  // Normalizar valores de 1-4 a percentil (0-100)
  const min = 0, max = 4;
  const percentil = v => v == null ? 0 : ((v - min) / (max - min)) * 100;
  const data = [
    { key: 'Goals',        value: percentil(dashboardData.mediaEquipo?.Goals),        color: '#4C87B2' },
    { key: 'Roles',        value: percentil(dashboardData.mediaEquipo?.Roles),        color: '#48BAA5' },
    { key: 'Processes',    value: percentil(dashboardData.mediaEquipo?.Processes),    color: '#595959' },
    { key: 'Interactions', value: percentil(dashboardData.mediaEquipo?.Interactions), color: '#e3147f' },
  ];

  // Tamaño igual al BarChart
  const sizeW = 1441;
  const sizeH = 721;
  const cx = sizeW / 2;
  const cy = sizeH / 2;
  const maxR = Math.min(sizeW, sizeH) * 0.4;
  const quarters = [
    [-Math.PI/2,    0         ],
    [ 0,             Math.PI/2],
    [ Math.PI/2,     Math.PI  ],
    [ Math.PI,     3*Math.PI/2]
  ];

  // 1) Guías con etiquetas de valor al 100% arriba
  const rings = [0.25,0.5,0.75,1].map((f,i) => {
    const r = maxR*f;
    return (
      <g key={i} opacity={0.3}>
        <circle
          cx={cx} cy={cy} r={r}
          fill="none" stroke="#8c8c8cff" strokeWidth="2"
        />
        <text
          x={cx}
          y={cy - r - 10}
          textAnchor="middle"
          alignmentBaseline="baseline"
          style={{ fontSize: 24, fill: '#373737ff' }}
        >
          {Math.round(f*100)}%
        </text>
      </g>
    );
  });

  // 2) Sectores coloreados
  const wedges = data.map((d,i) => {
    const [start,end] = quarters[i];
    const rOut = (d.value/100)*maxR;
    const x2 = cx + Math.cos(start)*rOut;
    const y2 = cy + Math.sin(start)*rOut;
    const x3 = cx + Math.cos(end  )*rOut;
    const y3 = cy + Math.sin(end  )*rOut;
    const path = [
      `M ${cx},${cy}`,
      `L ${x2},${y2}`,
      `A ${rOut},${rOut} 0 0,1 ${x3},${y3}`,
      `Z`
    ].join(' ');
    return (
      <path
        key={i}
        d={path}
        fill={d.color+'88'}    // opacity ~53%
        stroke={d.color}
        strokeWidth="4"
      />
    );
  });

  // 3) Etiquetas desplazadas más hacia afuera
  const labels = data.map((d,i) => {
    const [start,end] = quarters[i];
    const angle = (start+end)/2;
    const x = cx + Math.cos(angle)*(maxR + 100);
    const y = cy + Math.sin(angle)*(maxR + 100);
    const anchor = angle === 0 ? 'start' :
                   angle === Math.PI ? 'end' : 'middle';
    const baseline = angle === -Math.PI/2 ? 'baseline' :
                     angle === Math.PI/2  ? 'hanging'  :
                     'middle';
    return (
      <text
        key={i}
        x={x}
        y={y}
        textAnchor={anchor}
        alignmentBaseline={baseline}
        style={{ fontSize: 24, fill: '#333', fontWeight: 400 }}
      >
        {d.key}
      </text>
    );
  });

  return (
    <svg width={sizeW} height={sizeH} viewBox={`0 0 ${sizeW} ${sizeH}`}
      style={{ display: "block", background: "none" }}>
      {rings}
      {wedges}
      {labels}
    </svg>
  );
}
*/

export default ChartToImage;
